import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import logo from '../assets/logo.png';
import { ARCHIVE_RELAY_RESULTS, buildRelayResults } from './relayForLifeData';
import './relayForLifeExperience.css';

const TEAM_INSTITUTE_ID = '1f4ad70c-3092-4116-bdf4-0815e2a2a164';
const MEMBERS_INSTITUTE_ID = 'c829778b-75aa-49de-89eb-e90dcc705960';
const number = value => Math.round(value).toLocaleString('en-IE');
const compact = value => value >= 1_000_000
  ? `${(value / 1_000_000).toFixed(2)}M`
  : value >= 1_000 ? `${(value / 1_000).toFixed(1)}K` : number(value);
const normalise = value => value.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();

function useReducedMotion() {
  const [reduced, setReduced] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(preference.matches);
    preference.addEventListener('change', onChange);
    return () => preference.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

function AnimatedNumber({ value, reducedMotion, format = number }) {
  const [display, setDisplay] = useState(reducedMotion ? value : 0);
  const current = useRef(display);

  useEffect(() => {
    if (reducedMotion) {
      current.current = value;
      setDisplay(value);
      return undefined;
    }
    let frame;
    const from = current.current;
    const started = performance.now();
    const tick = now => {
      const progress = Math.min(1, (now - started) / 1100);
      const eased = 1 - Math.pow(1 - progress, 3);
      const next = Math.round(from + (value - from) * eased);
      current.current = next;
      setDisplay(next);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, reducedMotion]);

  return <span>{format(display)}</span>;
}

function RelayArt() {
  return <div className="relay-art" aria-hidden="true">
    <div className="relay-art-ring relay-art-ring-outer" />
    <div className="relay-art-ring relay-art-ring-inner" />
    <div className="relay-art-path" />
    <span className="relay-art-marker relay-art-marker-one" />
    <span className="relay-art-marker relay-art-marker-two" />
    <span className="relay-art-marker relay-art-marker-three" />
    <div className="relay-art-core"><span>🎗️</span><strong>24 hours</strong><small>moving together</small></div>
    <div className="relay-art-card relay-art-card-start"><strong>31 MAR</strong><span>12:00</span></div>
    <div className="relay-art-card relay-art-card-end"><strong>01 APR</strong><span>12:00</span></div>
  </div>;
}

function RankMark({ rank }) {
  return <span className={`relay-rank relay-rank-${rank <= 3 ? rank : 'other'}`} aria-label={`Rank ${rank}`}>
    {rank <= 3 ? ['🥇', '🥈', '🥉'][rank - 1] : rank}
  </span>;
}

function AthleteAvatar({ athlete }) {
  const initials = athlete.name.split(/\s+/).map(part => part[0]).slice(0, 2).join('').toUpperCase();
  return <span className="relay-athlete-avatar" aria-hidden="true">
    <span>{initials}</span>
    {athlete.imageUrl && <img src={athlete.imageUrl} alt="" loading="lazy" decoding="async" onError={event => { event.currentTarget.hidden = true; }} />}
  </span>;
}

export default function RelayForLifeExperience() {
  const [results, setResults] = useState(ARCHIVE_RELAY_RESULTS);
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem('spa-theme') !== 'light'; } catch { return true; }
  });
  const [tab, setTab] = useState('teams');
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    let active = true;
    async function loadResults() {
      try {
        const archiveIds = ARCHIVE_RELAY_RESULTS.athletes.map(athlete => athlete.id);
        const archiveProfiles = await supabase.from('profiles')
          .select('id,profile_img').in('id', archiveIds).eq('show_on_leaderboards', true);
        if (active && !archiveProfiles.error) {
          const imageById = new Map((archiveProfiles.data || []).map(profile => [profile.id, profile.profile_img]));
          setResults(previous => previous === ARCHIVE_RELAY_RESULTS ? {
            ...previous,
            athletes: previous.athletes.map(athlete => ({ ...athlete, imageUrl: imageById.get(athlete.id) || '' })),
          } : previous);
        }
        const [membersResponse, teamsResponse] = await Promise.all([
          supabase.from('user_institutes')
            .select('profile_id,institute_id,steps,team_id')
            .in('institute_id', [TEAM_INSTITUTE_ID, MEMBERS_INSTITUTE_ID])
            .eq('status', 'Active'),
          supabase.from('institute_teams')
            .select('id,name,image_url')
            .eq('institute_id', TEAM_INSTITUTE_ID),
        ]);
        if (membersResponse.error || teamsResponse.error) return;
        const members = membersResponse.data || [];
        const teams = teamsResponse.data || [];
        if (!members.length || !teams.length) return;
        const ids = [...new Set(members.map(member => member.profile_id).filter(Boolean))];
        const { data: profiles, error } = await supabase.from('profiles')
          .select('id,username,name,surname,profile_img,show_on_leaderboards').in('id', ids);
        if (error) return;
        const liveResults = buildRelayResults(teams, members, profiles || []);
        if (active && liveResults) setResults(liveResults);
      } catch {
        // The completed-event recap remains available when the archive cannot be queried.
      }
    }
    loadResults();
    return () => { active = false; };
  }, []);

  const entries = tab === 'teams' ? results.teams : results.athletes;
  const matching = useMemo(() => {
    const search = normalise(query);
    return search ? entries.filter(entry => normalise(entry.name).includes(search)) : entries;
  }, [entries, query]);
  const visible = query || showAll ? matching : matching.slice(0, 12);
  const leaderSteps = results.teams[0]?.steps || 1;

  function toggleTheme() {
    setIsDark(previous => {
      const next = !previous;
      try { localStorage.setItem('spa-theme', next ? 'dark' : 'light'); } catch { /* preference is optional */ }
      return next;
    });
  }

  function selectTab(next) {
    setTab(next);
    setQuery('');
    setShowAll(false);
  }

  return <div className={`relay-page ${isDark ? 'relay-dark' : 'relay-light'}`}>
    <header className="relay-header">
      <a className="relay-brand" href="/" aria-label="Snows ProAm home"><img src={logo} alt="" /><span>Snows ProAm</span></a>
      <div className="relay-header-actions">
        <span className="relay-complete"><span aria-hidden="true">●</span> 2026 event complete</span>
        <button type="button" className="relay-theme" onClick={toggleTheme} aria-label={`Switch to ${isDark ? 'light' : 'dark'} theme`} title={`Switch to ${isDark ? 'light' : 'dark'} theme`}>{isDark ? '☀' : '☾'}</button>
        <a className="relay-back" href="/">← <span>Back to Snows ProAm</span></a>
      </div>
    </header>

    <main>
      <section className="relay-hero" aria-labelledby="relay-title">
        <div className="relay-hero-inner">
          <div className="relay-hero-copy">
            <p className="relay-eyebrow"><span aria-hidden="true">🎗️</span> UCD Relay for Life · 2026 recap</p>
            <h1 id="relay-title">Every step stood for something bigger.</h1>
            <p className="relay-hero-sub">The Snows ProAm Steps Challenge brought teams together for a 24-hour movement in support of the Irish Cancer Society.</p>
            <div className="relay-hero-total"><span>Steps logged together</span><strong><AnimatedNumber value={results.totalSteps} reducedMotion={reducedMotion} /></strong></div>
            <div className="relay-hero-actions">
              <a className="relay-primary" href="#relay-results">Explore the results <span aria-hidden="true">↗</span></a>
              <a className="relay-secondary" href="https://linktr.ee/ucdrelayforlife" target="_blank" rel="noopener noreferrer">Relay for Life UCD <span aria-hidden="true">↗</span></a>
            </div>
          </div>
          <RelayArt />
        </div>
      </section>

      <div className="relay-main">
        <section className="relay-metrics" aria-label="2026 challenge results">
          {[
            { icon: '👥', value: results.athleteCount, label: 'Athletes', detail: 'who took part' },
            { icon: '🏆', value: results.teams.length, label: 'Teams', detail: 'moving together' },
            { icon: '📍', value: results.distanceKm, label: 'Kilometres', detail: 'estimated distance' },
            { icon: '🔥', value: results.calories, label: 'Calories', detail: 'estimated energy' },
          ].map(metric => <article className="relay-metric" key={metric.label}>
            <span className="relay-metric-icon" aria-hidden="true">{metric.icon}</span>
            <strong><AnimatedNumber value={metric.value} reducedMotion={reducedMotion} /></strong>
            <span className="relay-metric-label">{metric.label}</span>
            <small>{metric.detail}</small>
          </article>)}
        </section>

        <section className="relay-story" aria-labelledby="relay-story-title">
          <div><p className="relay-section-tag">A day worth remembering</p><h2 id="relay-story-title">24 hours. One shared cause.</h2><p>From midday on 31 March to midday on 1 April, every walk added to a community effort for people affected by cancer.</p></div>
          <div className="relay-story-facts"><span><strong>31 Mar – 1 Apr</strong>2026 challenge</span><span><strong>€100</strong>ClubHouse voucher prize</span><span><strong>Irish Cancer Society</strong>Event partner</span></div>
        </section>

        <section className="relay-results" id="relay-results" aria-labelledby="relay-results-title">
          <div className="relay-section-heading"><div><p className="relay-section-tag">The people who moved</p><h2 id="relay-results-title">See how everyone did.</h2><p>Explore the teams and individual athletes who took part.</p></div></div>

          <div className="relay-tabs" role="group" aria-label="Leaderboard view">
            <button type="button" aria-pressed={tab === 'teams'} className={tab === 'teams' ? 'is-active' : ''} onClick={() => selectTab('teams')}>Teams <span>{results.teams.length}</span></button>
            <button type="button" aria-pressed={tab === 'athletes'} className={tab === 'athletes' ? 'is-active' : ''} onClick={() => selectTab('athletes')}>Top athletes</button>
          </div>

          {tab === 'teams' && results.teams.length >= 3 && <div className="relay-podium" aria-label="Top three teams">
            {results.teams.slice(0, 3).map(team => <article className={`relay-podium-card relay-podium-${team.rank}`} key={team.id}>
              <RankMark rank={team.rank}/><div><img className="relay-podium-image" src={`/images/relay-team-${team.rank}.svg`} alt="" loading="lazy" decoding="async" /><span className="relay-podium-label">{team.rank === 1 ? 'Leading team' : `Place ${team.rank}`}</span><h3>{team.name}</h3><strong>{compact(team.steps)} <small>steps</small></strong></div>
              <div className="relay-podium-track" aria-hidden="true"><span style={{ '--bar-width': `${Math.round(team.steps / leaderSteps * 100)}%` }} /></div>
            </article>)}
          </div>}

          <div className="relay-list-panel">
            <div className="relay-list-toolbar"><div><h3>{tab === 'teams' ? 'All teams' : 'Top individual athletes'}</h3><p>{tab === 'teams' ? 'Ranked by steps recorded by their members.' : 'Individual steps recorded for the challenge.'}</p></div>
              <label className="relay-search"><span>Search {tab === 'teams' ? 'teams' : 'athletes'}</span><input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder={tab === 'teams' ? 'Find a team' : 'Find an athlete'} /></label>
            </div>
            <p className="relay-results-count" role="status">{query ? `${matching.length} ${tab === 'teams' ? (matching.length === 1 ? 'team' : 'teams') : (matching.length === 1 ? 'athlete' : 'athletes')} found` : `Showing ${visible.length} of ${entries.length} ${tab === 'teams' ? 'teams' : 'athletes'}`}</p>
            {visible.length ? <ol className="relay-list">
              {visible.map((entry, index) => {
                const rank = tab === 'teams' ? entry.rank : entries.indexOf(entry) + 1;
                const progress = Math.round(entry.steps / (tab === 'teams' ? leaderSteps : entries[0]?.steps || 1) * 100);
                return <li className="relay-list-row relay-image-row" key={entry.id || `${entry.name}-${index}`}>
                  <RankMark rank={rank}/>{tab === 'teams' ? <img className="relay-team-image" src={rank <= 3 ? `/images/relay-team-${rank}.svg` : entry.imageUrl} alt="" loading="lazy" decoding="async" /> : <AthleteAvatar athlete={entry} />}<div className="relay-list-name"><strong>{entry.name}</strong><span className="relay-list-track" aria-hidden="true"><span style={{ '--bar-width': `${progress}%` }} /></span></div>
                  <div className="relay-list-steps"><strong>{number(entry.steps)}</strong><span>{entry.steps ? 'steps' : 'no steps recorded'}</span></div>
                </li>;
              })}
            </ol> : <div className="relay-no-results">No matches for “{query}”. Try another name.</div>}
            {!query && entries.length > 12 && <button type="button" className="relay-show-all" aria-expanded={showAll} onClick={() => setShowAll(previous => !previous)}>{showAll ? 'Show fewer' : `Show all ${entries.length} ${tab}` } <span aria-hidden="true">↓</span></button>}
          </div>
        </section>

        <section className="relay-cause" aria-labelledby="relay-cause-title"><span className="relay-cause-icon" aria-hidden="true">🎗️</span><div><p className="relay-section-tag">Where athletes belong</p><h2 id="relay-cause-title">The challenge ended. The cause continues.</h2><p>Thank you to every athlete and team who walked, tracked and shared the journey.</p></div><a href="https://linktr.ee/ucdrelayforlife" target="_blank" rel="noopener noreferrer">Follow Relay for Life UCD ↗</a></section>
        <p className="relay-data-note">2026 event recap. Distance and calories are estimates based on recorded steps.</p>
      </div>
    </main>
  </div>;
}
